import React, { useState, useEffect } from "react";
import {
  Shield,
  Target,
  Eye,
  Heart,
  Award,
  ThumbsUp,
  Users,
  Briefcase,
  ChevronRight,
  Menu,
  X,
  MapPin,
  Phone,
  Mail,
  Globe,
  Calendar,
  Clock,
  CheckCircle,
  ArrowLeft,
  Lock,
  LogOut,
  FileText,
  Video,
  ExternalLink,
  User,
} from "lucide-react";

export default function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activePage, setActivePage] = useState("home"); // Estados: 'home', 'mision', 'vision', 'meta', 'valores', 'login', 'portal'

  // --- ESTADOS DEL LOGIN ---
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [authMessage, setAuthMessage] = useState({ type: "", text: "" });
  const [currentUser, setCurrentUser] = useState("Agente");

  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    correo: "",
    telefono: "",
  });
  const [formStatus, setFormStatus] = useState("idle"); // idle, submitting, success
  const [emailCopied, setEmailCopied] = useState(false);

  // Brand Colors
  const brandGreen = "#79b942";
  const brandDark = "#373a40";

  // Al cambiar de página virtual, hacer scroll al inicio
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activePage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("submitting");

    // URL OFICIAL DE GOHIGHLEVEL
    const webhookUrl =
      "https://services.leadconnectorhq.com/hooks/V8aoNO776WnHizgCyvUZ/webhook-trigger/0b8524d5-4a5e-49e4-95c3-7e01a692156c";

    try {
      const response = await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          first_name: formData.nombre,
          last_name: formData.apellido,
          email: formData.correo,
          phone: formData.telefono,
          tags: ["website_lead", "nueva_agencia"],
          source: "Formulario Web - Sé Parte de Nosotros",
        }),
      });

      if (response.ok) {
        setFormStatus("success");
        setFormData({ nombre: "", apellido: "", correo: "", telefono: "" });
        setTimeout(() => setFormStatus("idle"), 5000);
      } else {
        setFormStatus("idle");
      }
    } catch (error) {
      console.error("Error de conexión al enviar el formulario:", error);
      setFormStatus("idle");
    }
  };

  // Navegación inteligente
  const handleNavClick = (id) => {
    if (activePage !== "home") {
      setActivePage("home");
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  const handleCopyEmail = () => {
    const textArea = document.createElement("textarea");
    textArea.value = "admin@ariscorpagency.com";
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 3000);
    } catch (err) {
      console.error("Error al copiar el correo", err);
    }
    document.body.removeChild(textArea);
  };

  // --- FUNCIONES DEL LOGIN (CON CONTRASEÑA REFORZADA) ---
  const handleAuthSubmit = (e) => {
    e.preventDefault();
    const STRONG_PASSWORD = "ArisCorp$2026!";

    if (loginForm.password === STRONG_PASSWORD) {
      const username = loginForm.email.split("@")[0];
      setCurrentUser(username.charAt(0).toUpperCase() + username.slice(1));
      setIsAuthenticated(true);
      setActivePage("portal");
      setLoginForm({ email: "", password: "" });
      setAuthMessage({ type: "", text: "" });
    } else {
      setAuthMessage({
        type: "error",
        text: "Correo o contraseña incorrectos.",
      });
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setActivePage("home");
  };

  return (
    <div
      className="min-h-screen bg-white text-gray-800"
      style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
    >
      {/* NAVBAR ESTATICA (YA NO PERSIGUE NI TAPA NADA) */}
      <nav className="relative w-full z-50 pt-6 pb-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          {/* Logo GIGANTE */}
          <div
            className="flex items-center cursor-pointer py-1"
            onClick={() => handleNavClick("inicio")}
          >
            <img
              src="logo.svg"
              alt="ArisCorp Logo"
              className="h-32 sm:h-48 md:h-64 lg:h-72 w-auto object-contain origin-left transition-transform hover:scale-105"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
                document.getElementById("fallback-logo").style.display = "flex";
              }}
            />

            {/* Fallback Logo (por si falla el SVG) */}
            <div
              id="fallback-logo"
              className="hidden items-center space-x-2 ml-2"
            >
              <Shield className="w-12 h-12" style={{ color: brandGreen }} />
              <div>
                <h1
                  className="text-4xl font-bold leading-none tracking-tight"
                  style={{ color: brandDark }}
                >
                  Aris<span style={{ color: brandDark }}>Corp</span>
                </h1>
                <p
                  className="text-xs tracking-wider uppercase font-semibold"
                  style={{ color: brandGreen }}
                >
                  Agent Development
                </p>
              </div>
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex space-x-8 items-center text-sm">
            <button
              onClick={() => handleNavClick("inicio")}
              className="text-gray-700 hover:text-[#79b942] font-bold text-base transition-colors"
            >
              Inicio
            </button>
            <button
              onClick={() => handleNavClick("nosotros")}
              className="text-gray-700 hover:text-[#79b942] font-bold text-base transition-colors"
            >
              Sobre Nosotros
            </button>
            <button
              onClick={() => handleNavClick("eventos")}
              className="text-gray-700 hover:text-[#79b942] font-bold text-base transition-colors"
            >
              Eventos
            </button>

            {/* BOTÓN DE PORTAL DE AGENTES */}
            <button
              onClick={() =>
                setActivePage(isAuthenticated ? "portal" : "login")
              }
              className="text-gray-700 hover:text-[#79b942] font-bold text-base transition-colors flex items-center"
            >
              {isAuthenticated ? (
                <Shield className="w-5 h-5 mr-1" />
              ) : (
                <Lock className="w-5 h-5 mr-1" />
              )}{" "}
              Portal
            </button>

            <button
              onClick={() => handleNavClick("unete")}
              className="text-white px-6 py-3 rounded-full font-bold shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 text-base"
              style={{ backgroundColor: brandGreen }}
            >
              Sé Parte de Nosotros
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-700 p-2"
            >
              {isMobileMenuOpen ? (
                <X className="w-8 h-8" />
              ) : (
                <Menu className="w-8 h-8" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white shadow-xl z-50 border-t border-gray-100 pb-4">
            <div className="px-4 pt-2 space-y-2 flex flex-col">
              <button
                onClick={() => handleNavClick("inicio")}
                className="block px-3 py-3 text-left font-semibold text-gray-700"
              >
                Inicio
              </button>
              <button
                onClick={() => handleNavClick("nosotros")}
                className="block px-3 py-3 text-left font-semibold text-gray-700"
              >
                Sobre Nosotros
              </button>
              <button
                onClick={() => handleNavClick("eventos")}
                className="block px-3 py-3 text-left font-semibold text-gray-700"
              >
                Eventos
              </button>

              <button
                onClick={() =>
                  setActivePage(isAuthenticated ? "portal" : "login")
                }
                className="block px-3 py-3 text-left font-semibold text-[#79b942] flex items-center"
              >
                {isAuthenticated ? (
                  <Shield className="w-4 h-4 mr-2" />
                ) : (
                  <Lock className="w-4 h-4 mr-2" />
                )}{" "}
                Portal Agentes
              </button>

              <button
                onClick={() => handleNavClick("unete")}
                className="block px-3 py-3 text-left font-semibold text-white mt-2 rounded-lg"
                style={{ backgroundColor: brandGreen }}
              >
                Sé Parte de Nosotros
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* ========================================== */}
      {/* RENDERIZADO CONDICIONAL DE PÁGINAS         */}
      {/* ========================================== */}

      {activePage === "home" && (
        <>
          {/* SECTION 1: INICIO (Paddings reducidos porque la barra ya no ocupa espacio fijo) */}
          <section
            id="inicio"
            className="relative pt-8 pb-16 md:pt-12 md:pb-24 min-h-[85vh] flex items-center overflow-hidden bg-white"
          >
            <div
              className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full mix-blend-multiply filter blur-3xl opacity-10 pointer-events-none translate-x-1/2 -translate-y-1/2 animate-pulse"
              style={{ backgroundColor: brandGreen, animationDuration: "4s" }}
            ></div>
            <div
              className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full mix-blend-multiply filter blur-3xl opacity-5 pointer-events-none -translate-x-1/3 translate-y-1/3 animate-pulse"
              style={{ backgroundColor: brandDark, animationDuration: "5s" }}
            ></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid md:grid-cols-2 gap-12 items-center w-full">
              {/* Text Content */}
              <div className="space-y-6 z-20 text-center md:text-left group/herotext cursor-default">
                <div
                  className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-100 text-sm font-bold tracking-wide uppercase mb-2 shadow-sm transition-all duration-300 hover:bg-[#79b942] hover:text-white hover:scale-105 hover:shadow-md cursor-pointer"
                  style={{ color: brandGreen }}
                >
                  Agent Development Services
                </div>

                <h2
                  className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight tracking-tight uppercase transition-transform duration-700 group-hover/herotext:translate-x-2"
                  style={{ color: brandDark }}
                >
                  Construye
                  <br />
                  Tú Propia Agencia
                </h2>

                <p
                  className="text-4xl md:text-5xl font-light italic transition-all duration-700 group-hover/herotext:translate-x-2 group-hover/herotext:drop-shadow-[0_0_12px_rgba(121,185,66,0.4)]"
                  style={{ color: brandGreen }}
                >
                  Con Nosotros!!
                </p>

                <p className="text-lg md:text-xl text-gray-600 max-w-lg leading-relaxed pt-4 mx-auto md:mx-0 transition-transform duration-700 group-hover/herotext:translate-x-2">
                  Brindamos una oportunidad única para formar tu propia agencia
                  utilizando nuestras estrategias, herramientas y formación de
                  clase mundial.
                </p>

                <div className="pt-6 flex flex-col sm:flex-row gap-4 justify-center md:justify-start transition-transform duration-700 group-hover/herotext:translate-x-2">
                  <button
                    onClick={() => handleNavClick("unete")}
                    className="text-white text-lg font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-[0_10px_20px_-10px_rgba(121,185,66,0.8)] transition-all transform hover:-translate-y-1 flex items-center justify-center group"
                    style={{ backgroundColor: brandGreen }}
                  >
                    Comienza Hoy
                    <ChevronRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={() => handleNavClick("nosotros")}
                    className="bg-white text-lg font-bold px-8 py-4 rounded-full shadow-sm border border-gray-200 hover:bg-gray-50 transition-all flex items-center justify-center hover:-translate-y-1"
                    style={{ color: brandDark }}
                  >
                    Conoce Más
                  </button>
                </div>
              </div>

              {/* Image Content */}
              <div className="relative z-10 w-full mt-10 md:mt-0 group/heroimg cursor-default">
                <div
                  className="absolute inset-0 transform translate-x-4 translate-y-4 rounded-[2rem] md:rounded-[3rem] z-0 transition-all duration-700 group-hover/heroimg:translate-x-8 group-hover/heroimg:translate-y-8 group-hover/heroimg:rotate-2 group-hover/heroimg:opacity-80"
                  style={{ backgroundColor: brandDark }}
                ></div>

                <div className="relative z-10 h-[400px] md:h-[550px] rounded-[2rem] md:rounded-[3rem] overflow-hidden shadow-2xl border-4 border-white transition-all duration-700 group-hover/heroimg:border-[#79b942] group-hover/heroimg:-translate-y-2 group-hover/heroimg:shadow-[0_20px_50px_-12px_rgba(121,185,66,0.4)]">
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent z-10 transition-opacity duration-700 group-hover/heroimg:opacity-90"></div>
                  <img
                    src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                    alt="Business Training Latino"
                    className="w-full h-full object-cover transform transition-transform duration-1000 group-hover/heroimg:scale-110"
                  />

                  <div className="absolute bottom-6 left-6 z-20 bg-white p-4 rounded-2xl shadow-xl flex items-center space-x-3 transition-all duration-700 group-hover/heroimg:-translate-y-4 group-hover/heroimg:shadow-2xl">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-500 group-hover/heroimg:scale-110 group-hover/heroimg:rotate-12"
                      style={{ backgroundColor: `${brandGreen}20` }}
                    >
                      <Users
                        className="w-6 h-6 transition-colors duration-500"
                        style={{ color: brandGreen }}
                      />
                    </div>
                    <div>
                      <p className="font-bold text-gray-800 text-sm transition-colors duration-500 group-hover/heroimg:text-[#79b942]">
                        Formación Continua
                      </p>
                      <p className="text-xs text-gray-500">De Agentes a CEOs</p>
                    </div>
                  </div>
                </div>

                <div
                  className="absolute -top-4 -right-4 w-12 h-12 rounded-full z-20 border-4 border-white shadow-md transition-all duration-500 group-hover/heroimg:scale-[1.8] group-hover/heroimg:bg-white flex items-center justify-center overflow-hidden"
                  style={{ backgroundColor: brandGreen }}
                >
                  <Target className="w-5 h-5 text-[#79b942] opacity-0 transition-opacity duration-700 group-hover/heroimg:opacity-100 transform scale-50 group-hover/heroimg:scale-100" />
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 2: SOBRE NOSOTROS */}
          <section id="nosotros" className="py-24 bg-white relative z-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-20 relative group/about cursor-default">
                <div
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 md:w-64 md:h-64 rounded-full blur-3xl opacity-0 transition-opacity duration-1000 group-hover/about:opacity-60 -z-10"
                  style={{ backgroundColor: `${brandGreen}20` }}
                ></div>

                <h2
                  className="text-4xl font-extrabold transition-all duration-700 group-hover/about:-translate-y-1 group-hover/about:scale-105"
                  style={{ color: brandDark }}
                >
                  Sobre Nosotros
                </h2>

                <div
                  className="w-24 h-1 mt-4 mx-auto rounded-full transition-all duration-700 group-hover/about:w-48 group-hover/about:shadow-[0_0_15px_rgba(121,185,66,0.5)]"
                  style={{ backgroundColor: brandGreen }}
                ></div>

                <p className="mt-8 text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed transition-all duration-700 group-hover/about:translate-y-1 group-hover/about:text-gray-800">
                  ArisCorp se erige como un faro de empoderamiento, comprometido
                  a guiar a los agentes y agencias hacia el éxito a través de
                  nuestro lema:{" "}
                  <span
                    className="font-bold italic transition-all duration-500 inline-block group-hover/about:scale-105 group-hover/about:drop-shadow-[0_0_8px_rgba(121,185,66,0.3)]"
                    style={{ color: brandGreen }}
                  >
                    enseñar, inspirar, y transformar.
                  </span>
                </p>
              </div>

              <div
                className="rounded-3xl p-8 md:p-12 mb-20 relative overflow-hidden shadow-2xl transition-all duration-700 hover:-translate-y-2 hover:shadow-[0_20px_50px_-12px_rgba(121,185,66,0.4)]"
                style={{ backgroundColor: brandDark }}
              >
                <div className="absolute inset-0 opacity-15 bg-[url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80')] bg-cover bg-center grayscale transition-all duration-1000 scale-100 hover:scale-110 hover:opacity-30"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent"></div>

                <div className="relative z-10 grid md:grid-cols-2 gap-8 md:gap-12">
                  <div
                    onClick={() => setActivePage("mision")}
                    className="cursor-pointer p-8 md:p-10 rounded-2xl border border-gray-700 backdrop-blur-md transition-all transform hover:-translate-y-2 hover:border-[#79b942] group relative overflow-hidden shadow-xl"
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}
                  >
                    <div
                      className="absolute top-0 right-0 w-32 h-32 rounded-bl-full -mr-10 -mt-10 transition-transform duration-500 group-hover:scale-110 group-hover:bg-[#79b942]/30"
                      style={{ backgroundColor: `${brandGreen}15` }}
                    ></div>

                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-inner transition-transform group-hover:-translate-y-1 group-hover:scale-110 group-hover:shadow-[0_0_15px_rgba(121,185,66,0.5)]"
                      style={{ backgroundColor: `${brandGreen}20` }}
                    >
                      <Target
                        className="w-8 h-8 transition-colors group-hover:text-white"
                        style={{ color: brandGreen }}
                      />
                    </div>
                    <div className="flex justify-between items-center relative z-10 mb-4">
                      <h3 className="text-3xl font-bold text-white">
                        Nuestra Misión
                      </h3>
                      <ChevronRight className="w-6 h-6 text-[#79b942] opacity-0 -translate-x-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" />
                    </div>
                    <p className="text-gray-300 leading-relaxed text-lg relative z-10 group-hover:text-gray-100 transition-colors">
                      Equiparlo con conocimientos, estrategias y herramientas
                      esenciales, fomentando el crecimiento continuo y la
                      autenticidad en sus objetivos...{" "}
                      <span className="text-[#79b942] text-sm font-bold ml-1">
                        Ver más
                      </span>
                    </p>
                  </div>

                  <div
                    onClick={() => setActivePage("vision")}
                    className="cursor-pointer p-8 md:p-10 rounded-2xl border border-gray-700 backdrop-blur-md transition-all transform hover:-translate-y-2 hover:border-[#79b942] group relative overflow-hidden shadow-xl"
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}
                  >
                    <div
                      className="absolute top-0 right-0 w-32 h-32 rounded-bl-full -mr-10 -mt-10 transition-transform duration-500 group-hover:scale-110 group-hover:bg-[#79b942]/30"
                      style={{ backgroundColor: `${brandGreen}15` }}
                    ></div>

                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-inner transition-transform group-hover:-translate-y-1 group-hover:scale-110 group-hover:shadow-[0_0_15px_rgba(121,185,66,0.5)]"
                      style={{ backgroundColor: `${brandGreen}20` }}
                    >
                      <Eye
                        className="w-8 h-8 transition-colors group-hover:text-white"
                        style={{ color: brandGreen }}
                      />
                    </div>
                    <div className="flex justify-between items-center relative z-10 mb-4">
                      <h3 className="text-3xl font-bold text-white">
                        Nuestra Visión
                      </h3>
                      <ChevronRight className="w-6 h-6 text-[#79b942] opacity-0 -translate-x-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" />
                    </div>
                    <p className="text-gray-300 leading-relaxed text-lg relative z-10 group-hover:text-gray-100 transition-colors">
                      Establecer el estándar de excelencia en la industria de
                      seguros mediante la creación de agentes de clase mundial
                      que encarnen...{" "}
                      <span className="text-[#79b942] text-sm font-bold ml-1">
                        Ver más
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div
                onClick={() => setActivePage("meta")}
                className="cursor-pointer rounded-3xl p-12 md:p-16 text-center text-white mb-20 shadow-2xl relative overflow-hidden transition-all duration-700 hover:-translate-y-2 hover:shadow-[0_20px_50px_-12px_rgba(121,185,66,0.5)] hover:border hover:border-[#79b942]/50 group"
                style={{ backgroundColor: brandDark }}
              >
                <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80')] bg-cover bg-center grayscale transition-all duration-1000 group-hover:scale-110 group-hover:opacity-40"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>

                <div className="relative z-10 flex flex-col items-center">
                  <div
                    className="px-5 py-2 rounded-full mb-8 border border-opacity-50 shadow-sm transition-all duration-500 group-hover:-translate-y-2 group-hover:bg-[#79b942]/20 group-hover:scale-105 flex items-center"
                    style={{
                      backgroundColor: `${brandGreen}15`,
                      borderColor: brandGreen,
                    }}
                  >
                    <span
                      className="text-sm font-bold uppercase tracking-widest transition-colors duration-500 group-hover:text-white"
                      style={{ color: brandGreen }}
                    >
                      Nuestra Meta Principal
                    </span>
                  </div>

                  <h3 className="text-3xl md:text-4xl lg:text-5xl font-light italic mb-6 max-w-4xl leading-tight transition-transform duration-700 group-hover:scale-[1.03]">
                    "Transformar a agentes en{" "}
                    <span
                      className="font-bold transition-all duration-500 group-hover:drop-shadow-[0_0_10px_rgba(121,185,66,0.8)]"
                      style={{ color: brandGreen }}
                    >
                      profesionales
                    </span>
                    , y profesionales en{" "}
                    <span
                      className="font-bold transition-all duration-500 group-hover:drop-shadow-[0_0_10px_rgba(121,185,66,0.8)]"
                      style={{ color: brandGreen }}
                    >
                      CEO
                    </span>
                    "
                  </h3>

                  <div
                    className="w-16 h-1.5 rounded-full mt-6 transition-all duration-700 group-hover:w-40 group-hover:shadow-[0_0_15px_rgba(121,185,66,0.8)]"
                    style={{ backgroundColor: brandGreen }}
                  ></div>
                  <span className="mt-8 text-[#79b942] flex items-center font-bold tracking-widest uppercase text-sm opacity-0 transform translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
                    Conoce al Agente Ideal{" "}
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </span>
                </div>
              </div>

              <div
                onClick={() => setActivePage("valores")}
                className="cursor-pointer mt-20 relative rounded-3xl overflow-hidden shadow-2xl transition-all duration-700 hover:-translate-y-2 hover:shadow-[0_20px_50px_-12px_rgba(121,185,66,0.5)] group/main border border-transparent hover:border-[#79b942]/30"
                style={{ backgroundColor: brandDark }}
              >
                <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80')] bg-cover bg-center grayscale transition-all duration-1000 group-hover/main:scale-110 group-hover/main:opacity-30"></div>
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-transparent"></div>

                <div className="relative z-10 p-12 md:p-16">
                  <div className="text-center mb-16 transition-transform duration-700 group-hover/main:-translate-y-2">
                    <div
                      className="inline-block px-5 py-2 rounded-full mb-6 border border-opacity-50 shadow-sm transition-all duration-500 group-hover/main:scale-105 group-hover/main:bg-[#79b942]/20"
                      style={{
                        backgroundColor: `${brandGreen}15`,
                        borderColor: brandGreen,
                      }}
                    >
                      <span
                        className="text-sm font-bold uppercase tracking-widest transition-colors duration-500 group-hover/main:text-white"
                        style={{ color: brandGreen }}
                      >
                        Nuestra Esencia
                      </span>
                    </div>
                    <h3 className="text-4xl font-extrabold text-white mb-4 transition-transform duration-500 group-hover/main:scale-105 flex items-center justify-center">
                      Nuestros Valores Fundamentales{" "}
                      <ChevronRight className="w-8 h-8 ml-2 text-[#79b942] opacity-0 -translate-x-4 transition-all duration-500 group-hover/main:opacity-100 group-hover/main:translate-x-0" />
                    </h3>
                    <p className="text-lg text-gray-300 font-medium italic max-w-2xl mx-auto">
                      "Tus valores crean tu brújula interna que orienta el cómo
                      tomas decisiones." Haz clic para profundizar.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pointer-events-none">
                    {[
                      {
                        icon: <Award className="w-7 h-7" />,
                        title: "Misión sobre Comisión",
                      },
                      {
                        icon: <Shield className="w-7 h-7" />,
                        title: "Honestidad e Integridad",
                      },
                      {
                        icon: <ThumbsUp className="w-7 h-7" />,
                        title: "Actitud Positiva",
                      },
                      {
                        icon: <Users className="w-7 h-7" />,
                        title: "Respeto y Honor",
                      },
                      {
                        icon: <Briefcase className="w-7 h-7" />,
                        title: "Profesionalismo",
                      },
                      {
                        icon: <Heart className="w-7 h-7" />,
                        title: "Pasión y Compasión",
                      },
                    ].map((valor, idx) => (
                      <div
                        key={idx}
                        className="p-8 rounded-2xl border border-gray-700 backdrop-blur-sm transition-all transform group-hover/main:-translate-y-1 group-hover/main:border-[#79b942]/20 relative overflow-hidden"
                        style={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}
                      >
                        <div
                          className="w-14 h-14 rounded-full flex items-center justify-center mb-6 transition-colors duration-300 group-hover/main:bg-[#79b942]/20"
                          style={{ backgroundColor: `${brandGreen}20` }}
                        >
                          <div className="transition-colors duration-300 text-[#79b942] group-hover/main:text-[#79b942] drop-shadow-md">
                            {valor.icon}
                          </div>
                        </div>
                        <h4 className="text-xl font-bold text-white relative z-10">
                          {valor.title}
                        </h4>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION EVENTOS */}
          <section
            id="eventos"
            className="py-24 bg-gray-50 relative z-20 border-t border-gray-200"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16 relative group/events cursor-default">
                <div
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 md:w-64 md:h-64 rounded-full blur-3xl opacity-0 transition-opacity duration-1000 group-hover/events:opacity-60 -z-10"
                  style={{ backgroundColor: `${brandGreen}20` }}
                ></div>
                <h2
                  className="text-4xl font-extrabold transition-all duration-700 group-hover/events:-translate-y-1 group-hover/events:scale-105"
                  style={{ color: brandDark }}
                >
                  Próximos Eventos
                </h2>
                <div
                  className="w-24 h-1 mt-4 mx-auto rounded-full transition-all duration-700 group-hover/events:w-48 group-hover/events:shadow-[0_0_15px_rgba(121,185,66,0.5)]"
                  style={{ backgroundColor: brandGreen }}
                ></div>
              </div>

              <div
                className="rounded-3xl overflow-hidden shadow-2xl flex flex-col lg:flex-row transition-all duration-700 hover:-translate-y-2 hover:shadow-[0_20px_50px_-12px_rgba(121,185,66,0.4)] group/eventcard"
                style={{ backgroundColor: brandDark }}
              >
                <div
                  className="lg:w-5/12 relative overflow-hidden flex flex-col justify-end p-10 md:p-12 text-white min-h-[400px] lg:min-h-full"
                  style={{ backgroundColor: brandDark }}
                >
                  <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80')] bg-cover bg-center grayscale transition-all duration-1000 group-hover/eventcard:scale-110 group-hover/eventcard:opacity-30"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-black/50 to-black/10"></div>

                  <div className="relative z-10 transition-transform duration-700 group-hover/eventcard:-translate-y-2">
                    <div
                      className="inline-block px-4 py-1.5 rounded-full text-white text-xs font-bold tracking-widest uppercase mb-6 shadow-lg border backdrop-blur-sm animate-pulse"
                      style={{
                        backgroundColor: `${brandGreen}90`,
                        borderColor: brandGreen,
                      }}
                    >
                      ¡Cupo Limitado!
                    </div>
                    <h3 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight drop-shadow-lg">
                      Medicare
                      <br />
                      Academy
                    </h3>
                    <p className="text-xl font-medium text-gray-200 mb-2 drop-shadow-md">
                      Únete a nuestro equipo de Éxito, buscamos agentes de
                      seguros de alto nivel.
                    </p>
                  </div>
                </div>

                <div className="lg:w-7/12 p-8 sm:p-10 md:p-12 relative z-10 bg-[#2d3035] overflow-hidden">
                  <div
                    className="absolute top-0 right-0 w-32 h-32 sm:w-48 sm:h-48 lg:w-64 lg:h-64 rounded-bl-full -mr-8 -mt-8 sm:-mr-12 sm:-mt-12 lg:-mr-20 lg:-mt-20 opacity-10 transition-transform duration-700 group-hover/eventcard:scale-125"
                    style={{ backgroundColor: brandGreen }}
                  ></div>

                  <div className="relative z-10">
                    <div className="mb-8 p-4 rounded-xl border border-gray-700 bg-black/20 backdrop-blur-sm">
                      <p className="text-sm text-gray-300 font-semibold italic">
                        <span className="font-bold not-italic text-gray-100">
                          NO ES PARA TODOS
                        </span>{" "}
                        — Solo para personas profesionales y ambiciosas que
                        quieran resultados{" "}
                        <span
                          className="font-bold"
                          style={{ color: brandGreen }}
                        >
                          REALES
                        </span>
                        .
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                      {[
                        "Ingresos ilimitados",
                        "Carrera profesional",
                        "Entrenamiento incluido",
                        "Liderazgo y crecimiento",
                      ].map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center text-gray-200 transition-all duration-300 hover:translate-x-2"
                        >
                          <CheckCircle
                            className="w-5 h-5 mr-3 flex-shrink-0"
                            style={{ color: brandGreen }}
                          />
                          <span className="font-medium text-sm md:text-base">
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 mb-10 border-t border-gray-700 pt-8">
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <div className="p-2 rounded-lg bg-black/30 mr-4">
                            <Calendar
                              className="w-5 h-5"
                              style={{ color: brandGreen }}
                            />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">
                              Fecha
                            </p>
                            <p className="text-white font-semibold">
                              13 de Mayo, 2026
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="p-2 rounded-lg bg-black/30 mr-4">
                            <Clock
                              className="w-5 h-5"
                              style={{ color: brandGreen }}
                            />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">
                              Horarios
                            </p>
                            <p className="text-white text-sm">
                              <span className="text-gray-400">Registro:</span>{" "}
                              8:30 AM - 9:00 AM
                            </p>
                            <p className="text-white text-sm">
                              <span className="text-gray-400">Evento:</span>{" "}
                              9:00 AM - 4:00 PM
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="p-2 rounded-lg bg-black/30 mr-4">
                          <MapPin
                            className="w-5 h-5"
                            style={{ color: brandGreen }}
                          />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">
                            Lugar
                          </p>
                          <p className="text-white font-semibold mb-1">
                            Embassy Suites Hilton
                          </p>
                          <p className="text-gray-400 text-sm">
                            707 E Butterfield RD,
                            <br />
                            Lombard, IL 60148
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleNavClick("unete")}
                      className="w-full text-white font-bold text-lg py-4 rounded-xl shadow-[0_0_15px_rgba(121,185,66,0.3)] hover:shadow-[0_0_25px_rgba(121,185,66,0.5)] transition-all transform hover:-translate-y-1 flex justify-center items-center group cursor-pointer"
                      style={{ backgroundColor: brandGreen }}
                    >
                      Inscríbete Ahora
                      <ChevronRight className="ml-2 w-6 h-6 transition-transform group-hover:translate-x-1" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 3: ÚNETE */}
          <section
            id="unete"
            className="py-24 bg-gray-50 relative border-t border-gray-200"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col md:flex-row transition-all duration-700 hover:-translate-y-2 hover:shadow-[0_20px_50px_-12px_rgba(121,185,66,0.3)] group/formbox cursor-default">
                <div
                  className="md:w-5/12 p-12 flex flex-col justify-between text-white relative overflow-hidden"
                  style={{ backgroundColor: brandDark }}
                >
                  <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80')] bg-cover bg-center grayscale transition-all duration-1000 group-hover/formbox:scale-110 group-hover/formbox:opacity-30"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-black/50 to-black/10"></div>

                  <div className="relative z-10 transition-transform duration-700 group-hover/formbox:-translate-y-2">
                    <h2 className="text-4xl font-extrabold mb-6 leading-tight transition-all duration-500 group-hover/formbox:drop-shadow-[0_0_15px_rgba(121,185,66,0.5)]">
                      Sé Parte de Nuestra Historia
                    </h2>
                    <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                      Completa el formulario y descubre cómo ArisCorp puede
                      ayudarte a desarrollar todo tu potencial y lanzar tu
                      propia agencia de seguros con éxito.
                    </p>

                    <ul className="space-y-4 mb-8">
                      {[
                        "Capacitación Continua",
                        "Estrategias Comprobadas",
                        "Soporte y Herramientas",
                        "Crecimiento Exponencial",
                      ].map((item, i) => (
                        <li
                          key={i}
                          className="flex items-center text-gray-100 transition-all duration-300 hover:translate-x-2 cursor-default"
                        >
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center mr-3 shadow-md transition-transform duration-300 hover:scale-110"
                            style={{ backgroundColor: brandGreen }}
                          >
                            <svg
                              className="w-4 h-4 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="3"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                          <span className="font-semibold transition-colors hover:text-white">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="relative z-10 mt-12 pt-8 border-t border-gray-600 transition-all duration-500 group-hover/formbox:border-[#79b942]/50">
                    <h4
                      className="font-bold text-xl mb-2 transition-transform duration-500 group-hover/formbox:translate-x-2"
                      style={{ color: brandGreen }}
                    >
                      J. Arismendy Martínez
                    </h4>
                    <p className="text-gray-400 text-sm italic transition-all duration-500 group-hover/formbox:text-gray-300 group-hover/formbox:translate-x-2">
                      "Mi máxima aspiración es marcar una diferencia positiva en
                      las vidas de todas las personas."
                    </p>
                  </div>
                </div>

                <div className="md:w-7/12 p-12 lg:p-16">
                  <h3
                    className="text-3xl font-bold mb-8 transition-transform duration-700 group-hover/formbox:translate-x-2"
                    style={{ color: brandDark }}
                  >
                    Da el primer paso
                  </h3>

                  {formStatus === "success" ? (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center animate-fade-in">
                      <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <svg
                          className="w-8 h-8 text-green-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <h4 className="text-2xl font-bold text-gray-800 mb-2">
                        ¡Solicitud Enviada!
                      </h4>
                      <p className="text-gray-600">
                        Nos pondremos en contacto contigo muy pronto para
                        iniciar tu transformación.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label
                            className="block text-sm font-semibold text-gray-700 mb-2"
                            htmlFor="nombre"
                          >
                            Nombre
                          </label>
                          <input
                            type="text"
                            id="nombre"
                            name="nombre"
                            required
                            value={formData.nombre}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#79b942] outline-none transition-all"
                            placeholder="Tu nombre"
                          />
                        </div>
                        <div>
                          <label
                            className="block text-sm font-semibold text-gray-700 mb-2"
                            htmlFor="apellido"
                          >
                            Apellido
                          </label>
                          <input
                            type="text"
                            id="apellido"
                            name="apellido"
                            required
                            value={formData.apellido}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#79b942] outline-none transition-all"
                            placeholder="Tu apellido"
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          className="block text-sm font-semibold text-gray-700 mb-2"
                          htmlFor="correo"
                        >
                          Correo Electrónico
                        </label>
                        <input
                          type="email"
                          id="correo"
                          name="correo"
                          required
                          value={formData.correo}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#79b942] outline-none transition-all"
                          placeholder="ejemplo@correo.com"
                        />
                      </div>
                      <div>
                        <label
                          className="block text-sm font-semibold text-gray-700 mb-2"
                          htmlFor="telefono"
                        >
                          Número de Teléfono
                        </label>
                        <input
                          type="tel"
                          id="telefono"
                          name="telefono"
                          required
                          value={formData.telefono}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#79b942] outline-none transition-all"
                          placeholder="(123) 456-7890"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={formStatus === "submitting"}
                        className="w-full text-white font-bold text-lg py-4 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 mt-4 flex justify-center items-center"
                        style={{
                          backgroundColor: brandGreen,
                          opacity: formStatus === "submitting" ? 0.7 : 1,
                        }}
                      >
                        {formStatus === "submitting" ? (
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        ) : (
                          "Enviar Solicitud"
                        )}
                      </button>
                      <p className="text-xs text-center text-gray-500 mt-4">
                        Al enviar este formulario, aceptas que ArisCorp se ponga
                        en contacto contigo.
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ========================================== */}
      {/* PÁGINAS DE DETALLE (PÁGINAS VIRTUALES)     */}
      {/* ========================================== */}

      {/* PÁGINA: LOGIN DE AGENTES */}
      {activePage === "login" && (
        <section className="py-12 md:py-16 min-h-[80vh] flex items-center justify-center bg-white relative animate-fade-in overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-5 bg-[url('https://images.unsplash.com/photo-1573164574572-cb89e39749b4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80')] bg-cover bg-center"></div>
          <div className="max-w-md w-full mx-auto px-4 relative z-10">
            <button
              onClick={() => setActivePage("home")}
              className="flex items-center text-gray-500 hover:text-[#79b942] font-semibold mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />{" "}
              Volver al Inicio
            </button>
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 transition-all duration-500">
              <div
                className="p-8 md:p-10 transition-colors duration-500"
                style={{ backgroundColor: brandDark }}
              >
                <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-6 shadow-lg bg-white/20">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-center text-white mb-2">
                  Portal de Agentes
                </h2>
                <p className="text-center text-white/80 text-sm">
                  Ingresa tu correo y la clave de acceso autorizada.
                </p>
              </div>

              <div className="p-8 md:p-10">
                <form onSubmit={handleAuthSubmit} className="space-y-6">
                  {authMessage.text && (
                    <div
                      className={`p-4 border rounded-xl text-sm text-center font-medium bg-red-50 border-red-200 text-red-600`}
                    >
                      {authMessage.text}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Correo Electrónico
                    </label>
                    <input
                      type="email"
                      required
                      value={loginForm.email}
                      onChange={(e) =>
                        setLoginForm({ ...loginForm, email: e.target.value })
                      }
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#79b942] outline-none transition-all"
                      placeholder="agente@ejemplo.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Contraseña de Acceso
                    </label>
                    <input
                      type="password"
                      required
                      value={loginForm.password}
                      onChange={(e) =>
                        setLoginForm({ ...loginForm, password: e.target.value })
                      }
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-[#79b942] outline-none transition-all"
                      placeholder="••••••••"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full text-white font-bold text-lg py-4 rounded-xl shadow-lg transition-transform hover:-translate-y-1"
                    style={{ backgroundColor: brandGreen }}
                  >
                    Iniciar Sesión
                  </button>
                </form>

                <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                  <p className="text-sm font-medium text-gray-500">
                    ¿Aún no tienes tu contraseña? <br className="md:hidden" />
                    Escribe al correo:{" "}
                    <a
                      href="mailto:admin@ariscorpagency.com"
                      className="font-bold hover:underline transition-colors"
                      style={{ color: brandGreen }}
                    >
                      admin@ariscorpagency.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* PÁGINA: PORTAL DE AGENTES (DASHBOARD) */}
      {activePage === "portal" && isAuthenticated && (
        <section className="py-12 md:py-16 min-h-screen bg-gray-50 relative animate-fade-in">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <button
              onClick={() => setActivePage("home")}
              className="flex items-center text-gray-500 hover:text-[#79b942] font-semibold mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />{" "}
              Volver al Inicio
            </button>

            <div className="flex flex-col md:flex-row justify-between items-center mb-12 bg-white p-8 md:p-10 rounded-3xl shadow-md border border-gray-100">
              <div className="flex items-center mb-6 md:mb-0 w-full md:w-auto">
                <div
                  className="w-16 h-16 md:w-20 md:h-20 mr-6 flex-shrink-0 rounded-full flex items-center justify-center shadow-inner border-4 border-white ring-1 ring-gray-100"
                  style={{ backgroundColor: `${brandGreen}20` }}
                >
                  <User
                    className="w-8 h-8 md:w-10 md:h-10"
                    style={{ color: brandGreen }}
                  />
                </div>

                <div>
                  <h2
                    className="text-3xl md:text-4xl font-extrabold mb-1"
                    style={{ color: brandDark }}
                  >
                    Bienvenido, {currentUser}
                  </h2>
                  <p className="text-gray-500 font-medium text-sm md:text-base">
                    Panel de Herramientas y Recursos Integrados
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full md:w-auto flex items-center justify-center px-6 py-3 rounded-xl text-red-600 bg-red-50 hover:bg-red-100 font-semibold transition-colors shadow-sm"
              >
                <LogOut className="w-5 h-5 mr-2" /> Cerrar Sesión
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="space-y-8">
                <h3
                  className="text-xl font-bold flex items-center"
                  style={{ color: brandDark }}
                >
                  <Target
                    className="w-6 h-6 mr-2"
                    style={{ color: brandGreen }}
                  />{" "}
                  Plataformas CRM
                </h3>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <a
                    href="https://www.gohighlevel.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block p-4 mb-4 rounded-xl hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800 group-hover:text-[#79b942]">
                        Go High Level CRM
                      </span>
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#79b942]" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Plataforma todo en uno para marketing y automatización.
                    </p>
                  </a>
                  <a
                    href="https://www.agencybloc.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block p-4 mb-4 rounded-xl hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800 group-hover:text-[#79b942]">
                        Agency Bloc CRM
                      </span>
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#79b942]" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Gestión y monitoreo de clientes y comisiones.
                    </p>
                  </a>
                  <a
                    href="https://medicareproapp.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block p-4 rounded-xl hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800 group-hover:text-[#79b942]">
                        MedicarePRO CRM
                      </span>
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#79b942]" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      CRM especializado para agentes de Medicare.
                    </p>
                  </a>
                </div>
              </div>

              <div className="space-y-8">
                <h3
                  className="text-xl font-bold flex items-center"
                  style={{ color: brandDark }}
                >
                  <Video
                    className="w-6 h-6 mr-2"
                    style={{ color: brandGreen }}
                  />{" "}
                  Capacitación Continua
                </h3>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <a
                    href="https://themedicarelibrary.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block p-4 mb-4 rounded-xl hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800 group-hover:text-[#79b942]">
                        The Medicare Library
                      </span>
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#79b942]" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Biblioteca exclusiva de entrenamiento en video.
                    </p>
                  </a>
                  <a
                    href="https://www.examfx.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block p-4 rounded-xl hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800 group-hover:text-[#79b942]">
                        ExamFX
                      </span>
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#79b942]" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Proveedor de clases para Licencia de Salud y Vida.
                    </p>
                  </a>
                </div>
              </div>

              <div className="space-y-8">
                <h3
                  className="text-xl font-bold flex items-center"
                  style={{ color: brandDark }}
                >
                  <FileText
                    className="w-6 h-6 mr-2"
                    style={{ color: brandGreen }}
                  />{" "}
                  Cobertura E&O
                </h3>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="p-4 bg-[#79b942]/10 rounded-xl border border-[#79b942]/20 mb-4">
                    <p className="text-xs text-[#79b942] font-semibold">
                      Nota Importante:
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Requisito de 1 millón por cada Reclamo / 3 Millones
                      agregados.
                    </p>
                  </div>
                  <a
                    href="https://www.eoforless.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block p-4 mb-2 rounded-xl hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800 group-hover:text-[#79b942]">
                        E&O 4 Less
                      </span>
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#79b942]" />
                    </div>
                  </a>
                  <a
                    href="https://www.napa-benefits.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block p-4 rounded-xl hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800 group-hover:text-[#79b942]">
                        NAPA E&O
                      </span>
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#79b942]" />
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* PÁGINA: MISIÓN */}
      {activePage === "mision" && (
        <section className="py-12 md:py-16 min-h-[80vh] bg-white relative animate-fade-in overflow-hidden">
          <div
            className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full mix-blend-multiply filter blur-3xl opacity-5 pointer-events-none translate-x-1/2 -translate-y-1/2 animate-pulse"
            style={{ backgroundColor: brandGreen, animationDuration: "4s" }}
          ></div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <button
              onClick={() => setActivePage("home")}
              className="flex items-center text-gray-500 hover:text-[#79b942] font-semibold mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />{" "}
              Volver al Inicio
            </button>
            <div
              className="rounded-3xl p-10 md:p-16 shadow-2xl relative overflow-hidden transition-all duration-700 hover:-translate-y-2 hover:shadow-[0_20px_50px_-12px_rgba(121,185,66,0.3)] group/main"
              style={{ backgroundColor: brandDark }}
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#79b942] rounded-bl-full opacity-5 transition-transform duration-1000 group-hover/main:scale-125"></div>
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-8 shadow-lg transition-transform duration-500 group-hover/main:scale-110 group-hover/main:rotate-6"
                style={{ backgroundColor: `${brandGreen}20` }}
              >
                <Target
                  className="w-10 h-10 transition-colors duration-500 group-hover/main:text-white"
                  style={{ color: brandGreen }}
                />
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 transition-transform duration-500 group-hover/main:translate-x-2">
                Nuestra Misión
              </h2>
              <div
                className="w-24 h-1 mb-8 rounded-full shadow-[0_0_15px_rgba(121,185,66,0.5)] transition-all duration-700 group-hover/main:w-48"
                style={{ backgroundColor: brandGreen }}
              ></div>
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed font-light transition-transform duration-500 group-hover/main:translate-x-2">
                Nuestra misión es clara: equiparlo con conocimientos,
                estrategias y herramientas esenciales, fomentando el crecimiento
                continuo y la autenticidad en sus objetivos. Manteniendo valores
                de confianza, profesionalismo y honestidad, sentamos bases
                sólidas y fomentamos relaciones genuinas.
              </p>

              <div className="mt-12 p-8 rounded-2xl bg-white/5 border border-white/10 transition-all duration-500 hover:-translate-y-2 hover:bg-white/10 hover:shadow-[0_10px_30px_rgba(121,185,66,0.15)] group/card cursor-default">
                <h4 className="text-[#79b942] font-bold text-xl mb-3 flex items-center transition-transform duration-500 group-hover/card:translate-x-2">
                  <Shield className="w-6 h-6 mr-3 transition-transform duration-500 group-hover/card:scale-125 group-hover/card:rotate-12" />
                  Por Agentes Y Para Agentes
                </h4>
                <p className="text-gray-300 leading-relaxed group-hover/card:text-gray-100 transition-colors duration-300">
                  Nuestra organización está diseñada pensando en el agente
                  individual, por agentes que conocen y comprenden los desafíos
                  y luchas que enfrentan los agentes cuando comienzan sus
                  carreras y para aquellos con más experiencia que buscan elevar
                  sus carreras y pasar de ser agentes a profesionales de la
                  Industria.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* PÁGINA: VISIÓN */}
      {activePage === "vision" && (
        <section className="py-12 md:py-16 min-h-[80vh] bg-white relative animate-fade-in overflow-hidden">
          <div
            className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full mix-blend-multiply filter blur-3xl opacity-5 pointer-events-none translate-x-1/2 -translate-y-1/2 animate-pulse"
            style={{ backgroundColor: brandGreen, animationDuration: "4s" }}
          ></div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <button
              onClick={() => setActivePage("home")}
              className="flex items-center text-gray-500 hover:text-[#79b942] font-semibold mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />{" "}
              Volver al Inicio
            </button>
            <div
              className="rounded-3xl p-10 md:p-16 shadow-2xl relative overflow-hidden transition-all duration-700 hover:-translate-y-2 hover:shadow-[0_20px_50px_-12px_rgba(121,185,66,0.3)] group/main"
              style={{ backgroundColor: brandDark }}
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#79b942] rounded-bl-full opacity-5 transition-transform duration-1000 group-hover/main:scale-125"></div>
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-8 shadow-lg transition-transform duration-500 group-hover/main:scale-110 group-hover/main:rotate-6"
                style={{ backgroundColor: `${brandGreen}20` }}
              >
                <Eye
                  className="w-10 h-10 transition-colors duration-500 group-hover/main:text-white"
                  style={{ color: brandGreen }}
                />
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 transition-transform duration-500 group-hover/main:translate-x-2">
                Nuestra Visión
              </h2>
              <div
                className="w-24 h-1 mb-8 rounded-full shadow-[0_0_15px_rgba(121,185,66,0.5)] transition-all duration-700 group-hover/main:w-48"
                style={{ backgroundColor: brandGreen }}
              ></div>
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed font-light space-y-6">
                <span className="block transition-transform duration-500 group-hover/main:translate-x-2">
                  Nuestra visión es establecer el estándar de excelencia en la
                  industria de seguros mediante la creación de agentes de clase
                  mundial que encarnen integridad, profesionalismo y calidad.
                  Estamos comprometidos a reparar el daño causado por practices
                  poco éticas y restaurar la fe en el liderazgo de las agencias.
                </span>
                <span className="block transition-transform duration-500 group-hover/main:translate-x-2 delay-75">
                  A través de un enfoque centrado en los agentes, priorizamos
                  las necesidades y el éxito de nuestros agentes, asegurándonos
                  de que tengan las mejores herramientas, capacitación y soporte
                  para atender a sus clientes de manera efectiva.
                </span>
                <span className="block font-medium text-white italic p-6 rounded-2xl bg-white/5 border border-white/10 transition-all duration-500 hover:-translate-y-1 hover:bg-white/10 hover:shadow-lg hover:border-[#79b942]/50 cursor-default">
                  "Para los agentes y por los agentes, nos esforzamos por
                  construir una comunidad donde la conducta ética y el servicio
                  superior sean las bases de nuestro éxito, impulsando cambios
                  positivos y confianza dentro de la industria."
                </span>
              </p>
            </div>
          </div>
        </section>
      )}

      {/* PÁGINA: META (EL AGENTE PROFESIONAL) */}
      {activePage === "meta" && (
        <section className="py-12 md:py-16 min-h-[80vh] bg-white relative animate-fade-in overflow-hidden">
          <div
            className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full mix-blend-multiply filter blur-3xl opacity-5 pointer-events-none translate-x-1/2 -translate-y-1/2 animate-pulse"
            style={{ backgroundColor: brandGreen, animationDuration: "4s" }}
          ></div>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <button
              onClick={() => setActivePage("home")}
              className="flex items-center text-gray-500 hover:text-[#79b942] font-semibold mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />{" "}
              Volver al Inicio
            </button>

            <div className="text-center mb-16 group/head cursor-default">
              <div
                className="inline-block px-5 py-2 rounded-full mb-6 border shadow-sm bg-[#79b942]/10 transition-transform duration-500 group-hover/head:scale-105"
                style={{ borderColor: brandGreen }}
              >
                <span
                  className="text-sm font-bold uppercase tracking-widest"
                  style={{ color: brandGreen }}
                >
                  Nuestra Meta Principal
                </span>
              </div>
              <h2
                className="text-4xl md:text-5xl font-extrabold mb-6 transition-transform duration-500 group-hover/head:-translate-y-1"
                style={{ color: brandDark }}
              >
                Transformar a agentes en profesionales,
                <br />y profesionales en CEO.
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto transition-transform duration-500 group-hover/head:translate-y-1">
                Un agente de seguros ideal es un profesional que no sólo
                sobresale en su conocimiento técnico y habilidades de ventas,
                sino que también encarna cualidades que generan confianza y
                relación con los clientes.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Experiencia Profesional",
                  icon: <Briefcase />,
                  list: [
                    "Conocedor de diversos productos",
                    "Acreditado y actualizado",
                    "Habilidades analíticas para evaluar riesgos",
                    "Tech-Savvy: utiliza tecnología moderna",
                  ],
                },
                {
                  title: "Habilidades Interpersonales",
                  icon: <Users />,
                  list: [
                    "Empático y escucha activamente",
                    "Comunicativo con términos complejos",
                    "Agradable: construye relaciones sólidas",
                    "Paciente al abordar dudas",
                  ],
                },
                {
                  title: "Estándares Éticos",
                  icon: <Shield />,
                  list: [
                    "Integridad y transparencia total",
                    "Centrado en los intereses del cliente",
                    "Confiabilidad en cumplir promesas",
                  ],
                },
                {
                  title: "Orientado a Servicios",
                  icon: <Heart />,
                  list: [
                    "Responsivo en brindar respuestas",
                    "Proactivo revisando coberturas",
                    "Solucionador de problemas ágil",
                  ],
                },
                {
                  title: "Visión para Ventas",
                  icon: <Target />,
                  list: [
                    "Orientado a lograr objetivos",
                    "Activo en adquisición con marketing",
                    "Activo en retención de la cartera",
                  ],
                },
                {
                  title: "Mejora Continua",
                  icon: <Award />,
                  list: [
                    "Aprendiz permanente en desarrollo",
                    "Adaptable a tendencias de industria",
                    "Compromiso con la excelencia",
                  ],
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="p-8 rounded-2xl border border-gray-200 shadow-lg bg-white hover:shadow-[0_20px_40px_-15px_rgba(121,185,66,0.2)] hover:border-[#79b942]/50 transition-all duration-500 hover:-translate-y-2 group/card cursor-default"
                >
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6 bg-gray-50 group-hover/card:bg-[#79b942] transition-colors duration-500 shadow-sm">
                    <div className="text-gray-400 group-hover/card:text-white transition-colors duration-500">
                      {item.icon}
                    </div>
                  </div>
                  <h3
                    className="text-2xl font-bold mb-5 transition-transform duration-300 group-hover/card:translate-x-1"
                    style={{ color: brandDark }}
                  >
                    {item.title}
                  </h3>

                  <ul className="space-y-4">
                    {item.list.map((li, i) => (
                      <li
                        key={i}
                        className="flex items-start text-gray-600 transition-all duration-300 hover:translate-x-2 group/item"
                      >
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center mr-3 shadow-md transition-transform duration-300 group-hover/item:scale-110 flex-shrink-0 mt-0.5"
                          style={{ backgroundColor: brandGreen }}
                        >
                          <svg
                            className="w-4 h-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="3"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <span className="text-sm font-medium transition-colors group-hover/item:text-gray-900 leading-relaxed">
                          {li}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PÁGINA: VALORES (FILOSOFÍA EMPRESARIAL) */}
      {activePage === "valores" && (
        <section className="py-12 md:py-16 min-h-[80vh] bg-white relative animate-fade-in overflow-hidden">
          <div
            className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full mix-blend-multiply filter blur-3xl opacity-5 pointer-events-none translate-x-1/2 -translate-y-1/2 animate-pulse"
            style={{ backgroundColor: brandGreen, animationDuration: "4s" }}
          ></div>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <button
              onClick={() => setActivePage("home")}
              className="flex items-center text-gray-500 hover:text-[#79b942] font-semibold mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />{" "}
              Volver al Inicio
            </button>

            <div className="text-center mb-16 group/head cursor-default">
              <h2
                className="text-4xl md:text-5xl font-extrabold mb-4 transition-transform duration-500 group-hover/head:-translate-y-1"
                style={{ color: brandDark }}
              >
                Nuestra Filosofía Empresarial
              </h2>
              <div
                className="w-24 h-1 mx-auto rounded-full mb-6 transition-all duration-500 group-hover/head:w-48 group-hover/head:shadow-[0_0_15px_rgba(121,185,66,0.5)]"
                style={{ backgroundColor: brandGreen }}
              ></div>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto transition-transform duration-500 group-hover/head:translate-y-1">
                Nuestra Filosofía Empresarial abarca nuestras creencias, valores
                y principios fundamentales que guían nuestras operaciones,
                comportamiento e interacciones con agentes y clientes.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  icon: <Award className="w-8 h-8" />,
                  title: "Misión sobre Comisión",
                  text: "No podemos servir a dos amos; hacerlo significa que inevitablemente honraremos a uno y deshonraremos al otro. Debemos elegir entre servir al dinero y cumplir nuestra misión. Al perseguir apasionadamente nuestra misión, naturalmente logramos una excelente compensación. Prioriza tu misión y todo lo demás seguirá.",
                },
                {
                  icon: <Shield className="w-8 h-8" />,
                  title: "Honestidad e Integridad",
                  text: "La honestidad es hacer que tus palabras se sujeten a la realidad y la integridad es hacer que la realidad se ajuste a tus palabras.",
                },
                {
                  icon: <ThumbsUp className="w-8 h-8" />,
                  title: "Actitud Mental Positiva",
                  text: "Una actitud mental positiva te da poder sobre las circunstancias en vez de que las circunstancias tenga poder sobre ti. Tu actitud es una expresión de tus valores, creencias y expectativas.",
                },
                {
                  icon: <Users className="w-8 h-8" />,
                  title: "Respeto y Honor",
                  text: "El Respetar es entender que cada persona tiene un valor inherente. La función de cada organismo es vital, y cada individuo es mutuamente dependiente. Entendemos que nadie ha merecido honor por algo que recibió sino por algo que entregó, aportó, sacrificó o logró.",
                },
                {
                  icon: <Briefcase className="w-8 h-8" />,
                  title: "Profesionalismo y Lealtad",
                  text: "Comportamientos y actitudes que siguen normas de respeto, objetividad y eficiencia en el trabajo. Lealtad es la actitud de quien no engaña, traiciona o abandona a sus amigos y superiores, y de quien se mantiene firme en sus ideales y convicciones.",
                },
                {
                  icon: <Heart className="w-8 h-8" />,
                  title: "Pasión y Compasión",
                  text: "Pasión es un deseo ardiente de cumplir tu misión actuando con diligencia. Compartiendo para las necesidades de todos; practicando la hospitalidad. Celebra los logros de los demás y con los demás. Unánimes, mansos y humildes de corazón.",
                },
              ].map((val, idx) => (
                <div
                  key={idx}
                  className="p-8 rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-[0_20px_40px_-15px_rgba(121,185,66,0.2)] transition-all duration-500 hover:-translate-y-2 group/card flex flex-col sm:flex-row gap-6 items-start cursor-default"
                >
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-500 group-hover/card:scale-110 group-hover/card:rotate-6 group-hover/card:bg-[#79b942] group-hover/card:shadow-lg"
                    style={{ backgroundColor: `${brandGreen}20` }}
                  >
                    <div className="transition-colors duration-500 text-[#79b942] group-hover/card:text-white">
                      {val.icon}
                    </div>
                  </div>
                  <div className="transition-transform duration-500 group-hover/card:translate-x-2">
                    <h3
                      className="text-2xl font-bold mb-3"
                      style={{ color: brandDark }}
                    >
                      {val.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{val.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer
        className="relative py-16 overflow-hidden group/footer"
        style={{ backgroundColor: brandDark }}
      >
        <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80')] bg-cover bg-center grayscale transition-all duration-1000 group-hover/footer:scale-110 group-hover/footer:opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="lg:col-span-1 flex flex-col justify-center">
              <p className="text-gray-300 text-sm leading-relaxed mb-3 italic">
                "Transformando a agentes en profesionales, y profesionales en
                CEO."
              </p>
            </div>

            <a
              href="https://maps.google.com/?q=2210+W+North+Ave,+Melrose+Park,+IL+60160"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-6 rounded-2xl border border-gray-700 backdrop-blur-sm transition-all transform hover:-translate-y-1 hover:border-[#79b942]/50 group relative overflow-hidden shadow-lg cursor-pointer"
              style={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}
            >
              <div
                className="absolute top-0 right-0 w-16 h-16 rounded-bl-full -mr-4 -mt-4 transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundColor: `${brandGreen}15` }}
              ></div>
              <MapPin
                className="w-6 h-6 mb-4 transition-transform group-hover:-translate-y-1"
                style={{ color: brandGreen }}
              />
              <h4 className="text-white font-bold mb-1 relative z-10">
                Ubicación
              </h4>
              <p className="text-gray-400 text-sm transition-colors group-hover:text-gray-200 relative z-10">
                2210 W North Ave,
                <br />
                Melrose Park Il 60160
              </p>
            </a>

            <a
              href="https://wa.me/16308813673"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-6 rounded-2xl border border-gray-700 backdrop-blur-sm transition-all transform hover:-translate-y-1 hover:border-[#79b942]/50 group relative overflow-hidden shadow-lg cursor-pointer"
              style={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}
            >
              <div
                className="absolute top-0 right-0 w-16 h-16 rounded-bl-full -mr-4 -mt-4 transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundColor: `${brandGreen}15` }}
              ></div>
              <Phone
                className="w-6 h-6 mb-4 transition-transform group-hover:-translate-y-1"
                style={{ color: brandGreen }}
              />
              <h4 className="text-white font-bold mb-1 relative z-10">
                Teléfono / WhatsApp
              </h4>
              <p className="text-gray-400 text-sm transition-colors group-hover:text-gray-200 relative z-10">
                630-881-3673
              </p>
            </a>

            <a
              href="mailto:admin@ariscorpagency.com"
              onClick={handleCopyEmail}
              className="block p-6 rounded-2xl border border-gray-700 backdrop-blur-sm transition-all transform hover:-translate-y-1 hover:border-[#79b942]/50 group relative overflow-hidden shadow-lg cursor-pointer"
              style={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}
            >
              <div
                className="absolute top-0 right-0 w-16 h-16 rounded-bl-full -mr-4 -mt-4 transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundColor: `${brandGreen}15` }}
              ></div>
              <Mail
                className="w-6 h-6 mb-4 transition-transform group-hover:-translate-y-1"
                style={{ color: brandGreen }}
              />
              <h4 className="text-white font-bold mb-1 relative z-10">
                {emailCopied ? (
                  <span style={{ color: brandGreen }}>¡Copiado!</span>
                ) : (
                  "Correo Electrónico"
                )}
              </h4>
              <p className="text-gray-400 text-sm transition-colors group-hover:text-gray-200 relative z-10">
                admin@ariscorpagency.com
              </p>
            </a>
          </div>

          <div className="border-t border-gray-700/50 pt-8 text-sm text-center text-gray-500">
            &copy; {new Date().getFullYear()} ArisCorp Agent Development
            Services. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
